import api from './api'
import type {
  Request,
  CreateRequestRequest,
  ApproveRequestRequest,
  AdjustApprovedRequest,
  SecondApproverOverrideRequest,
  UpdateAdvancePaymentFlagsRequest,
  RequestListParams,
  LeaveBalance,
} from '@/types/request.types'
import type { ApiResponse, PaginatedResponse } from '@/types/api.types'

export const requestsService = {
  /** Filtrelenmiş talep listesini PDF olarak indirir */
  async downloadListPdf(filters?: Record<string, unknown>): Promise<Blob> {
    const response = await api.get('/requests/export/pdf', {
      params: filters,
      responseType: 'blob',
      timeout: 2 * 60 * 1000,
    })
    return response.data
  },

  async getAll(params?: RequestListParams): Promise<ApiResponse<PaginatedResponse<Request>>> {
    const response = await api.get('/requests', { params })
    return response.data
  },

  async getById(id: string): Promise<ApiResponse<Request>> {
    const response = await api.get(`/requests/${id}`)
    return response.data
  },

  async create(
    request: CreateRequestRequest,
    reportFile?: File | null,
  ): Promise<ApiResponse<Request>> {
    if (reportFile) {
      const fd = new FormData()
      fd.append('type', request.type)
      fd.append('startDate', request.startDate)
      fd.append('endDate', request.endDate)
      if (request.reason) fd.append('reason', request.reason)
      if (request.amount != null) fd.append('amount', String(request.amount))
      if (request.leaveTypeId) fd.append('leaveTypeId', request.leaveTypeId)
      if (request.signatureBase64) fd.append('signatureBase64', request.signatureBase64)
      if (request.leaveFormBase64) fd.append('leaveFormBase64', request.leaveFormBase64)
      fd.append('report', reportFile)
      const response = await api.post('/requests', fd)
      return response.data
    }
    const response = await api.post('/requests', request)
    return response.data
  },

  /** 3. aşamada takılı kalmış talepleri ileri taşı — apply=false önizleme */
  async clearLegacyStageThree(apply: boolean): Promise<any> {
    const response = await api.post('/requests/maintenance/clear-stage-3', { apply })
    return response.data
  },

  async approve(id: string, body: ApproveRequestRequest = {}): Promise<ApiResponse<Request>> {
    const response = await api.post(`/requests/${id}/approve`, body)
    return response.data
  },

  /** İzin gün önizleme: takvim / pazar / resmi tatil / net iş günü */
  async leaveDaysPreview(
    startDate: string,
    endDate: string,
  ): Promise<{ calendarDays: number; sundays: number; holidays: number; workingDays: number }> {
    const response = await api.get('/requests/leave-days-preview', { params: { startDate, endDate } })
    return response.data?.data ?? response.data
  },

  async updateAdvancePaymentFlags(
    id: string,
    body: UpdateAdvancePaymentFlagsRequest,
  ): Promise<ApiResponse<Request>> {
    const response = await api.patch(`/requests/${id}/advance-payment-flags`, body)
    return response.data
  },

  async reject(id: string, reason?: string): Promise<ApiResponse<Request>> {
    const response = await api.post(`/requests/${id}/reject`, { reason: reason || undefined })
    return response.data
  },

  /** Onaylanmış avans, bordroya işlenmeden önce İK Müdürü tarafından reddedilir */
  async rejectApprovedAdvance(id: string, reason?: string): Promise<ApiResponse<Request>> {
    const response = await api.post(`/requests/${id}/reject-approved-advance`, {
      reason: reason || undefined,
    })
    return response.data
  },

  async adjustAfterApproval(id: string, body: AdjustApprovedRequest): Promise<ApiResponse<Request>> {
    const response = await api.patch(`/requests/${id}/adjust-after-approval`, body)
    return response.data
  },

  async revokeRejectionAndAdjust(id: string, body: AdjustApprovedRequest): Promise<ApiResponse<Request>> {
    const response = await api.patch(`/requests/${id}/revoke-rejection-adjust`, body)
    return response.data
  },

  async secondApproverOverride(
    id: string,
    body: SecondApproverOverrideRequest,
  ): Promise<ApiResponse<Request>> {
    const response = await api.post(`/requests/${id}/second-approver-override`, body)
    return response.data
  },

  async updateOwn(id: string, body: Partial<CreateRequestRequest>, reportFile?: File | null): Promise<ApiResponse<Request>> {
    if (reportFile) {
      const fd = new FormData()
      if (body.type) fd.append('type', body.type)
      if (body.startDate) fd.append('startDate', body.startDate)
      if (body.endDate) fd.append('endDate', body.endDate)
      if (body.reason !== undefined) fd.append('reason', body.reason ?? '')
      if (body.amount != null) fd.append('amount', String(body.amount))
      if (body.attachmentUrl) fd.append('attachmentUrl', body.attachmentUrl)
      fd.append('report', reportFile)
      const response = await api.patch(`/requests/${id}`, fd)
      return response.data
    }
    const response = await api.patch(`/requests/${id}`, body)
    return response.data
  },

  async cancel(id: string): Promise<ApiResponse<Request>> {
    const response = await api.post(`/requests/${id}/cancel`, {})
    return response.data
  },

  async getMyRequests(): Promise<ApiResponse<PaginatedResponse<Request>>> {
    const response = await api.get('/requests/my-requests')
    return response.data
  },

  async getByEmployee(employeeId: string): Promise<ApiResponse<{ items: Request[]; total: number }>> {
    const response = await api.get(`/requests/by-employee/${employeeId}`)
    return response.data
  },

  async getLeaveBalance(employeeId: string): Promise<ApiResponse<LeaveBalance[]>> {
    const response = await api.get(`/requests/leave-balance/${employeeId}`)
    return response.data
  },

  async getPendingApprovals(): Promise<ApiResponse<Request[]>> {
    const response = await api.get('/requests/pending-approvals')
    return response.data
  },

  /** Şirketteki tüm bekleyen talepler — onay yetkisi olan rollere açık. */
  async getPending(opts?: { skipLoading?: boolean }): Promise<{ data: Request[] }> {
    const response = await api.get('/requests/pending', { skipLoading: opts?.skipLoading })
    // Backend transform interceptor genellikle { success, data } sarmalar.
    const payload = response.data
    if (payload && Array.isArray(payload.data)) {
      return { data: payload.data as Request[] }
    }
    if (Array.isArray(payload)) {
      return { data: payload as Request[] }
    }
    return { data: [] }
  },
}
